// frontend/src/components/orcamento/CenarioPilares.js
// Card "Cenário por pilar" da aba Simulação: uma linha por departamento com
// receita e despesa P50, o resultado em barra ponto-e-bigode (P10–P90, ponto no
// P50, linha do zero), margem P50 e chance de prejuízo. Clicar na linha abre a
// composição do pilar (categorias de receita e despesa que o alimentam).
// Os números já chegam deslocados pelas edições (hook).
//
// Escala: única para todas as linhas, mas calculada SEM o "Sem departamento"
// (folha, impostos etc. o deixam muito negativo e espremiam os demais perto do
// zero). A linha dele é cortada na borda com o aviso "fora da escala".
// A barra é HTML com posições em %: acompanha a largura da coluna e o ponto
// continua redondo (SVG esticado distorceria o círculo).
import React, { useEffect, useState } from 'react';
import { Empresas } from './GridProjecao';

const fmt = (v) => `${v < 0 ? '−' : ''}${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(Math.abs(v || 0))}`;
const pct1 = (x) => `${(x * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
const TOP = 8;          // categorias visíveis por lado antes do "+ N outras"
const FOLGA = 0.08;     // respiro da escala em cada ponta
const SEM = 'Sem departamento';

// escala [lo, hi] dos pilares com departamento (ou de todos, se só houver o '')
function escala(pilares) {
  const base = pilares.filter((p) => p.codigo !== '');
  const lista = base.length ? base : pilares;
  const lo0 = Math.min(0, ...lista.map((p) => p.resultado.p10));
  const hi0 = Math.max(0, ...lista.map((p) => p.resultado.p90));
  const folga = ((hi0 - lo0) || 1) * FOLGA;
  return [lo0 - folga, hi0 + folga];
}

// barra de uma linha: bigode P10–P90, ponto no P50, linha do zero; corta o que sai da escala
function Barra({ r, lo, hi }) {
  const span = (hi - lo) || 1;
  const pos = (v) => ((v - lo) / span) * 100;
  const cl = (v) => Math.min(100, Math.max(0, pos(v)));
  const foraEsq = r.p10 < lo;
  const foraDir = r.p90 > hi;
  const pontoDentro = r.p50 >= lo && r.p50 <= hi;
  const ini = cl(r.p10), fim = cl(r.p90);
  return (
    <div className="op-cp-trilho" role="img" aria-label={`Resultado de ${fmt(r.p10)} a ${fmt(r.p90)}, P50 ${fmt(r.p50)}`}>
      <span className="zero" style={{ left: `${cl(0)}%` }}></span>
      {fim > ini && <span className="bigode" style={{ left: `${ini}%`, width: `${fim - ini}%` }}></span>}
      {pontoDentro && <span className="ponto" style={{ left: `${pos(r.p50)}%` }}></span>}
      {foraEsq && <span className="fora esq">◀ fora da escala</span>}
      {foraDir && <span className="fora dir">fora da escala ▶</span>}
    </div>
  );
}

// lista de UMA natureza dentro do pilar
function Lado({ titulo, natureza, itens, verTodos, onVerTodos, onAbrirCategoria, siglas }) {
  const subtotal = itens.reduce((s, x) => s + (x.valor || 0), 0);
  const vis = verTodos ? itens : itens.slice(0, TOP);
  const resto = itens.slice(TOP);
  return (
    <div className="op-cp-lado">
      <div className="op-cp-lado-head"><span>{titulo}</span><b>{fmt(subtotal)}</b></div>
      {!itens.length && <div className="text-muted small">Nenhuma categoria neste pilar.</div>}
      {vis.map((x) => (
        <div key={x.codigo} className="op-cp-item">
          {x.cat?.sintetica || String(x.codigo).startsWith('premissa:')
            ? <span className="op-cp-cat sint" title="Vem das premissas"><span className="op-selo-p">P</span>{x.nome}</span>
            : (
              <button type="button" className="op-cp-cat" onClick={() => onAbrirCategoria(natureza, x.cat)}
                title={x.cat?.origens ? 'Abrir na empresa de origem' : 'Abrir a análise da categoria'}>
                {!x.cat?.origens && <span className="cod">{x.codigo}</span>}{x.nome}
                <Empresas origens={x.cat?.origens} siglas={siglas} />
              </button>
            )}
          {x.valor === null
            ? <span className="op-cp-semvalor">{siglas ? 'sem valor nas empresas' : 'sem valor (digite na grade)'}</span>
            : <span className="v">{fmt(x.valor)}<em>{subtotal > 0 ? pct1(x.valor / subtotal) : '—'}</em></span>}
        </div>
      ))}
      {resto.length > 0 && (
        <button type="button" className="op-cp-mais" onClick={onVerTodos}>
          {verTodos ? 'mostrar só as principais'
            : `+ ${resto.length} outras (${fmt(resto.reduce((s, x) => s + (x.valor || 0), 0))})`}
        </button>
      )}
    </div>
  );
}

// grupo: visão consolidada (títulos "do grupo" e selos das empresas nas categorias)
export default function CenarioPilares({ pilares, composicaoPilar, onAbrirCategoria, grupo = false, siglas = null }) {
  const [hover, setHover] = useState(null);
  const [aberto, setAberto] = useState(null);           // código do pilar aberto ('' vale)
  const [verTodos, setVerTodos] = useState({});         // { receita: bool, despesa: bool }
  useEffect(() => { setVerTodos({}); }, [aberto]);
  if (!pilares.length) return null;

  const [lo, hi] = escala(pilares);
  const zeroPct = Math.min(100, Math.max(0, ((0 - lo) / ((hi - lo) || 1)) * 100));
  const temSemDepto = pilares.some((p) => p.codigo === '');
  const alternar = (cod) => setAberto((a) => (a === cod ? null : cod));

  return (
    <div className="pa-card op-cp">
      <div className="pa-card-head">
        <div className="pa-card-title">Cenário por pilar{grupo ? ' do grupo' : ''}</div>
        <div className="pa-card-sub">resultado de cada departamento nos cenários simulados (barra P10–P90, ponto no P50) · clique para ver a composição</div>
      </div>
      <div className="op-cp-rolagem">
        <table className="op-table">
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Pilar</th>
              <th className="num">Receita P50</th>
              <th className="num">Despesa P50</th>
              <th className="op-cp-faixa">
                {/* rótulo do zero, uma vez só, alinhado com a linha das barras */}
                <div className="op-cp-trilho cab"><span className="zero-rot" style={{ left: `${zeroPct}%` }}>0</span></div>
              </th>
              <th className="num">Resultado P50</th>
              <th className="num">Margem P50</th>
              <th className="num">Chance de prejuízo</th>
            </tr>
          </thead>
          <tbody>
            {pilares.map((p, i) => {
              const r = p.resultado;
              const margem = p.receitaP50 > 0 ? r.p50 / p.receitaP50 : null;
              const alto = p.probPrejuizo !== null && p.probPrejuizo > 0.5;
              const nome = p.codigo ? p.nome : SEM;
              const estaAberto = aberto === p.codigo;
              const comp = estaAberto ? composicaoPilar(p.codigo) : null;
              const subRec = comp ? comp.receita.reduce((s, x) => s + (x.valor || 0), 0) : 0;
              const subDesp = comp ? comp.despesa.reduce((s, x) => s + (x.valor || 0), 0) : 0;
              return (
                <React.Fragment key={p.codigo || '__sem__'}>
                  <tr className={`op-cp-linha ${estaAberto ? 'aberta' : ''}`} role="button" tabIndex={0} aria-expanded={estaAberto}
                    onClick={() => alternar(p.codigo)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); alternar(p.codigo); } }}
                    onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
                    <td className="op-cp-nome">
                      <span className="op-cp-chev" aria-hidden="true">{estaAberto ? '▾' : '▸'}</span>{nome}
                    </td>
                    <td className="num">{fmt(p.receitaP50)}</td>
                    <td className="num">{fmt(p.despesaP50)}</td>
                    <td className="op-cp-faixa">
                      <Barra r={r} lo={lo} hi={hi} />
                      {hover === i && (
                        <div className="op-dica">
                          <div className="op-dica-titulo">{nome}</div>
                          <div>Resultado P10 <b>{fmt(r.p10)}</b> · P50 <b>{fmt(r.p50)}</b> · P90 <b>{fmt(r.p90)}</b></div>
                          <div>Receita P50 <b>{fmt(p.receitaP50)}</b> · Despesa P50 <b>{fmt(p.despesaP50)}</b></div>
                        </div>
                      )}
                    </td>
                    <td className={`num ${r.p50 < 0 ? 'neg' : ''}`} style={{ fontWeight: 700 }}>{fmt(r.p50)}</td>
                    <td className="num">{margem === null ? '—' : pct1(margem)}</td>
                    <td className="num">
                      {p.probPrejuizo === null ? '—' : `${Math.round(p.probPrejuizo * 100)}%`}
                      {alto && (
                        <span className="op-cp-alto" title="Mais da metade dos cenários fecha com prejuízo">
                          <i className="fas fa-triangle-exclamation me-1"></i>alta
                        </span>
                      )}
                    </td>
                  </tr>
                  {estaAberto && comp && (
                    <tr className="op-cp-detalhe">
                      <td colSpan={7}>
                        <div className="op-cp-lados">
                          <Lado titulo="Receitas" natureza="receita" itens={comp.receita} verTodos={!!verTodos.receita}
                            onVerTodos={() => setVerTodos((v) => ({ ...v, receita: !v.receita }))} onAbrirCategoria={onAbrirCategoria} siglas={siglas} />
                          <Lado titulo="Despesas" natureza="despesa" itens={comp.despesa} verTodos={!!verTodos.despesa}
                            onVerTodos={() => setVerTodos((v) => ({ ...v, despesa: !v.despesa }))} onAbrirCategoria={onAbrirCategoria} siglas={siglas} />
                        </div>
                        <div className={`op-cp-res ${subRec - subDesp < 0 ? 'neg' : ''}`}>
                          Resultado do pilar (valores da grade, com as suas edições): <b>{fmt(subRec - subDesp)}</b>
                          <span className="text-muted"> · a coluna Resultado P50 vem das simulações</span>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {temSemDepto && (
        <div className="pa-card-body op-cp-nota">
          Sem departamento reúne o que não tem rateio de departamento na Omie (folha, impostos, DAS etc.); por isso
          costuma aparecer com prejuízo. A escala das barras não o considera; a linha dele aparece cortada.
        </div>
      )}
    </div>
  );
}
